import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { PhotoType, StepType, VehicleType } from "../utils/types"

type PhotoTypeMap = Record<
  PhotoType,
  {
    categoryName: string
    guide: string
    name: string
    stepType: StepType
  }
>
/* 
In order to prevent TS errors, just a subset of VehicleType is used here
otherwise the translations would need to be defined for all VehicleTypes
event those which we dont use currently
*/
export type VehicleTypeSubset = Extract<VehicleType, "PersonalVehicle" | "Motorcycle" | "AgriculturalMachine">

export function isSupportedVehicleType(vehicleType?: VehicleType): vehicleType is VehicleTypeSubset {
  return vehicleType === "PersonalVehicle" || vehicleType === "Motorcycle" || vehicleType === "AgriculturalMachine"
}

export function useGetStepTexts(vehicleType?: VehicleType) {
  const { t } = useTranslation()

  return useCallback(
    (photoType: PhotoType) => {
      const vehicleTypeComputed: VehicleTypeSubset = isSupportedVehicleType(vehicleType)
        ? vehicleType
        : "PersonalVehicle"

      const config: PhotoTypeMap = {
        FrontRightSide: {
          name: t("phototype.frontRightSide.name"),
          categoryName: t(`phototype.${vehicleTypeComputed}.category.carview`),
          guide: t(`phototype.${vehicleTypeComputed}.frontRightSide.guide`),
          stepType: "VehiclePhoto",
        },
        FrontLeftSide: {
          name: t("phototype.frontLeftSide.name"),
          categoryName: t(`phototype.${vehicleTypeComputed}.category.carview`),
          guide: t(`phototype.${vehicleTypeComputed}.frontLeftSide.guide`),
          stepType: "VehiclePhoto",
        },
        RearLeftSide: {
          name: t("phototype.rearLeftSide.name"),
          categoryName: t(`phototype.${vehicleTypeComputed}.category.carview`),
          guide: t(`phototype.${vehicleTypeComputed}.rearLeftSide.guide`),
          stepType: "VehiclePhoto",
        },
        RearRightSide: {
          name: t("phototype.rearRightSide.name"),
          categoryName: t(`phototype.${vehicleTypeComputed}.category.carview`),
          guide: t(`phototype.${vehicleTypeComputed}.rearRightSide.guide`),
          stepType: "VehiclePhoto",
        },
        FrontSide: {
          name: t("phototype.frontSide.name"),
          categoryName: t(`phototype.${vehicleTypeComputed}.category.carview`),
          guide: t(`phototype.${vehicleTypeComputed}.frontSide.guide`),
          stepType: "VehiclePhoto",
        },
        LeftSide: {
          name: t("phototype.leftSide.name"),
          categoryName: t(`phototype.${vehicleTypeComputed}.category.carview`),
          guide: t(`phototype.${vehicleTypeComputed}.leftSide.guide`),
          stepType: "VehiclePhoto",
        },
        RearSide: {
          name: t("phototype.rearSide.name"),
          categoryName: t(`phototype.${vehicleTypeComputed}.category.carview`),
          guide: t(`phototype.${vehicleTypeComputed}.rearSide.guide`),
          stepType: "VehiclePhoto",
        },
        RightSide: {
          name: t("phototype.rightSide.name"),
          categoryName: t(`phototype.${vehicleTypeComputed}.category.carview`),
          guide: t(`phototype.${vehicleTypeComputed}.rightSide.guide`),
          stepType: "VehiclePhoto",
        },
        Motor: {
          name: t("phototype.motor.name"),
          categoryName: t("phototype.category.exterior"),
          guide: t("phototype.motor.guide"),
          stepType: "VehiclePhoto",
        },
        Windscreen: {
          name: t(`phototype.${vehicleTypeComputed}.windscreen.name`),
          categoryName: t("phototype.category.exterior"),
          guide: t(`phototype.${vehicleTypeComputed}.windscreen.guide`),
          stepType: "VehiclePhoto",
        },
        WindscreenFromInside: {
          name: t(`phototype.${vehicleTypeComputed}.windscreenFromInside.name`),
          categoryName: t("phototype.category.interior"),
          guide: t(`phototype.${vehicleTypeComputed}.windscreenFromInside.guide`),
          stepType: "VehiclePhoto",
        },
        // Advanced windscreen capture is car-only, so these texts have no vehicleType variants
        WindscreenDriver: {
          name: t("phototype.windscreenDriver.name"),
          categoryName: t("phototype.category.exterior"),
          guide: t("phototype.windscreenDriver.guide"),
          stepType: "VehiclePhoto",
        },
        WindscreenPassenger: {
          name: t("phototype.windscreenPassenger.name"),
          categoryName: t("phototype.category.exterior"),
          guide: t("phototype.windscreenPassenger.guide"),
          stepType: "VehiclePhoto",
        },
        Vin: {
          name: t(`phototype.${vehicleTypeComputed}.vin.name`),
          categoryName: t("phototype.category.exterior"),
          guide: t(`phototype.${vehicleTypeComputed}.vin.guide`),
          stepType: "VehiclePhoto",
        },
        Dash: {
          name: t("phototype.dash.name"),
          categoryName: t("phototype.category.interior"),
          guide: t("phototype.dash.guide"),
          stepType: "VehiclePhoto",
        },
        Odometer: {
          name: t("phototype.odometer.name"),
          categoryName: t("phototype.category.interior"),
          guide: t("phototype.odometer.guide"),
          stepType: "VehiclePhoto",
        },
        Cockpit: {
          name: t("phototype.cockpit.name"),
          categoryName: t("phototype.category.exterior"),
          guide: t("phototype.cockpit.guide"),
          stepType: "VehiclePhoto",
        },
        Keys: {
          name: t("phototype.keys.name"),
          categoryName: t("phototype.category.interior"),
          guide: t("phototype.keys.guide"),
          stepType: "VehiclePhoto",
        },
        ExtraEquipment: {
          name: t("phototype.extra.equipment.name"),
          categoryName: t("phototype.category.extra.equipment"),
          guide: "",
          stepType: "ExtraEquipment",
        },
        Damage: {
          name: t("phototype.damage.name"),
          categoryName: t("phototype.category.damage"),
          guide: "",
          stepType: "Damage",
        },
        Claim: {
          name: t("phototype.claim.name"),
          categoryName: t("phototype.category.claim"),
          guide: "",
          stepType: "Claim",
        },
        VehicleRegistrationCertificateFront: {
          name: t("phototype.registrationfront.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.registrationfront.guide"),
          stepType: "DocumentPhoto",
        },
        VehicleRegistrationCertificateBack: {
          name: t("phototype.registrationback.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.registrationback.guide"),
          stepType: "DocumentPhoto",
        },
        RoadworthinessCertificateFront: {
          name: t("phototype.roadworthinessfront.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.roadworthinessfront.guide"),
          stepType: "DocumentPhoto",
        },
        RoadworthinessCertificateBack: {
          name: t("phototype.roadworthinessback.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.roadworthinessback.guide"),
          stepType: "DocumentPhoto",
        },
        Invoice: {
          name: t("phototype.invoice.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.invoice.guide"),
          stepType: "DocumentPhoto",
        },
        AcceptanceProtocol: {
          name: t("phototype.transferprotocol.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.transferprotocol.guide"),
          stepType: "DocumentPhoto",
        },
        TransferProtocol: {
          name: t("phototype.transferprotocol.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.transferprotocol.guide"),
          stepType: "DocumentPhoto",
        },
        IdentityCardFront: {
          name: t("phototype.identityCardFront.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.identityCardFront.guide"),
          stepType: "DocumentPhoto",
        },
        IdentityCardBack: {
          name: t("phototype.identityCardBack.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.identityCardBack.guide"),
          stepType: "DocumentPhoto",
        },
        AccidentStatementFront: {
          name: t("phototype.accidentStatementFront.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.accidentStatement.guide"),
          stepType: "DocumentPhoto",
        },
        DriversLicenseFront: {
          name: t("phototype.driversLicenseFront.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.driversLicenseFront.guide"),
          stepType: "DocumentPhoto",
        },
        DriversLicenseBack: {
          name: t("phototype.driversLicenseBack.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.driversLicenseBack.guide"),
          stepType: "DocumentPhoto",
        },
        OtherDocument: {
          name: t("phototype.otherdocument.name"),
          categoryName: t("phototype.category.documents"),
          guide: t("phototype.otherdocument.guide"),
          stepType: "DocumentPhoto",
        },
        ExtraClientData: {
          name: "",
          categoryName: "",
          guide: "",
          stepType: "ExtraClientData",
        },
        MotorVideo: {
          name: t("phototype.motorVideo.name"),
          categoryName: t("phototype.category.exterior"),
          guide: t("phototype.motorVideo.guide"),
          stepType: "VehicleVideo",
        },
        DashVideo: {
          name: t("phototype.dashVideo.name"),
          categoryName: t("phototype.category.interior"),
          guide: t("phototype.dashVideo.guide"),
          stepType: "VehicleVideo",
        },
        OdometerVideo: {
          name: t("phototype.odometerVideo.name"),
          categoryName: t("phototype.category.interior"),
          guide: t("phototype.odometerVideo.guide"),
          stepType: "VehicleVideo",
        },
      }

      return config[photoType]
    },
    [t, vehicleType],
  )
}

type StepTypeIntroSubset = Extract<StepType, "ExtraEquipment" | "Damage" | "DocumentPhoto">

type IntroTextMap = Record<
  StepTypeIntroSubset,
  {
    title: string
    primaryActionTitle: string
    text: string
  }
>

type UseStepTypeIntroText = {
  stepType: StepTypeIntroSubset
  vehicleType?: VehicleType
}

export function useScreenTypeIntroText(params: UseStepTypeIntroText) {
  const { stepType, vehicleType } = params

  const { t } = useTranslation()

  const vehicleTypeComputed: VehicleTypeSubset = isSupportedVehicleType(vehicleType) ? vehicleType : "PersonalVehicle"

  const translations: IntroTextMap = {
    ExtraEquipment: {
      title: t("extra.equipment.intro.title"),
      text: t(`extra.equipment.${vehicleTypeComputed}.intro.text`),
      primaryActionTitle: t("extra.equipment.intro.primaryActionTitle"),
    },
    Damage: {
      title: t("damage.intro.title"),
      text: t(`damage.${vehicleTypeComputed}.intro.text`),
      primaryActionTitle: t("damage.intro.primaryActionTitle"),
    },
    DocumentPhoto: {
      title: t("documents.intro.title"),
      text: t("documents.intro.text"),
      primaryActionTitle: t("documents.intro.button.continue"),
    },
  }

  return translations[stepType]
}
